import localforage from 'localforage';
import axios from 'axios';
import customerCacheService from './customerCacheService';

const productStore = localforage.createInstance({
  name: 'dukakit_pos',
  storeName: 'products'
});

const CACHE_PRODUCTS    = 'products';
const CACHE_CATEGORIES  = 'categories';
const CACHE_META        = 'catalog_meta';
const TIMESTAMP_KEY     = 'products_last_updated';
const IDENTITY_KEY      = 'products_identity';
const TTL               = 15 * 60 * 1000; // 15 minutes

// Identity the cache was populated for (company/branch). If the logged-in
// identity changes (branch switch, or a different user logging in on the
// same device) without an explicit clearCache() call happening first, the
// cache must never be served — it belongs to someone else's tenant data.
function currentIdentity() {
  try {
    const userDetails = JSON.parse(window.localStorage.getItem('userDetails'));
    return (userDetails && userDetails.company && userDetails.company.id) || null;
  } catch (e) {
    return null;
  }
}

// Only retain POS-relevant fields; strip everything else before storage
// to keep the IndexedDB blob as small as possible.
const POS_FIELDS = new Set([
  'id', 'product', 'sku', 'ppu', 'wholesale_sp', 'wholesale_ppu',
  'bg_color', 'remaining_units', 'is_service', 'productcat_id',
  'can_be_sold', 'is_directly_proportional', 'affects_profit',
  'affects_inventory', 'more_details', 'img',
  'effective_price', 'campaign_label',
  'is_grouped', 'is_manufactured',
  // Product Variations + Units/Packaging - see PosService::decorateWithVariantsAndUnits().
  'has_variants', 'units', 'variants',
]);

function slimProduct(p) {
  const out = {};
  for (const k of POS_FIELDS) {
    if (k in p) out[k] = p[k];
  }
  // Object.freeze() prevents Vue 2 from walking the object tree to add
  // reactive getters/setters — eliminates O(n × fields) overhead on load.
  return Object.freeze(out);
}

export default {
  /**
   * Returns the hot catalog (top ~2000 products by order frequency).
   *
   * For very large catalogs this is intentionally NOT the full catalog.
   * Text search and category browsing of the full catalog is handled
   * server-side via /api/pos/search and /api/pos/products.
   */
  async getProducts(force = false, bustBackendCache = false, silent = false) {
    const now          = Date.now();
    const lastUpdated  = await productStore.getItem(TIMESTAMP_KEY);
    const cachedForId  = await productStore.getItem(IDENTITY_KEY);
    const identityMatches = cachedForId && cachedForId === currentIdentity();
    const isFresh      = lastUpdated && identityMatches && (now - lastUpdated < TTL);

    if (!force && isFresh) {
      const cached = await productStore.getItem(CACHE_PRODUCTS);
      if (cached) return cached;
    }

    try {
      // _silent: the global axios interceptor (app.js) toggles a page-wide
      // loading indicator on every request/response pair. This call can fire
      // as an invisible background refresh (see Salespage.vue's mounted()
      // "silently confirm we're current" comment) - without _silent, that
      // background refresh visibly re-triggers the loader right after the
      // page already finished loading from cache, which is what "silently"
      // was supposed to mean but never actually suppressed.
      const response   = await axios.get('/api/pos/catalog/hot', {
        params: { limit: 2000, ...(bustBackendCache ? { force: 1 } : {}) },
        _silent: silent,
      });
      const products   = response.data.products   || [];
      const categories = response.data.allcategories || [];
      const meta       = {
        is_partial_catalog: response.data.is_partial_catalog ?? false,
        total_catalog_size: response.data.total_catalog_size ?? products.length,
        hot_limit:          response.data.hot_limit ?? 2000,
        loaded_at:          now,
      };

      const slim = products.map(slimProduct);

      await productStore.setItem(CACHE_PRODUCTS,   slim);
      await productStore.setItem(CACHE_CATEGORIES, categories);
      await productStore.setItem(CACHE_META,        meta);
      await productStore.setItem(TIMESTAMP_KEY,     now);
      await productStore.setItem(IDENTITY_KEY,      currentIdentity());

      return slim;
    } catch (e) {
      console.warn('POS: backend fetch failed, using cached products');
      // Only fall back to the cache if it belongs to the currently logged-in
      // identity — never serve another company's cached catalog just because
      // the network request failed.
      if (identityMatches) {
        const fallback = await productStore.getItem(CACHE_PRODUCTS);
        if (fallback) return fallback;
      }
      throw new Error('Unable to load products and no cached data available.');
    }
  },

  async getCategories() {
    return productStore.getItem(CACHE_CATEGORIES);
  },

  /** Returns metadata about the catalog (partial vs full, total size). */
  async getMeta() {
    return productStore.getItem(CACHE_META);
  },

  async hotReloadLocalDB(bustBackendCache = false, silent = false) {
    await this.clearCache();
    await this.getProducts(true, bustBackendCache, silent);
    await customerCacheService.getCustomers('', true, silent);
  },

  /**
   * Patches a single product in the cached catalog in place, for realtime
   * single-product sync (ProductUpdated) instead of refetching/recaching the
   * entire hot catalog on every change.
   *
   * Merges onto whatever's already cached for this id rather than replacing
   * it outright: rawProduct comes from a plain product lookup and doesn't
   * carry computed fields like effective_price/campaign_label that only the
   * hot-catalog endpoint's pricing pipeline attaches — merging preserves
   * those instead of dropping them until the next full reload.
   *
   * Returns the merged, frozen product so the caller can use the exact same
   * object for in-memory state, keeping IndexedDB and Vue state in sync.
   */
  async patchProduct(rawProduct) {
    const fresh  = slimProduct(rawProduct);
    const cached = await productStore.getItem(CACHE_PRODUCTS);

    // No catalog cached yet (e.g. cleared, or this tab never finished its
    // initial load) — nothing to patch in place. Return the fresh product
    // for in-memory use without fabricating a one-item "catalog".
    if (!Array.isArray(cached)) {
      return Object.freeze(fresh);
    }

    const idx = cached.findIndex(p => p.id === fresh.id);
    const merged = idx !== -1
      ? Object.freeze({ ...cached[idx], ...fresh })
      : Object.freeze(fresh);

    const updated = cached.slice();
    if (idx !== -1) {
      updated[idx] = merged;
    } else {
      updated.push(merged);
    }
    await productStore.setItem(CACHE_PRODUCTS, updated);

    return merged;
  },

  /**
   * Removes one or more products from the cached catalog in place, mirroring
   * patchProduct's read-modify-write but for deletions instead of upserts.
   */
  async removeProducts(productIds) {
    const idSet = new Set(productIds);
    const cached = await productStore.getItem(CACHE_PRODUCTS);
    if (!Array.isArray(cached)) return;

    const updated = cached.filter(p => !idSet.has(p.id));
    await productStore.setItem(CACHE_PRODUCTS, updated);
  },

  async clearCache() {
    await productStore.removeItem(CACHE_PRODUCTS);
    await productStore.removeItem(CACHE_CATEGORIES);
    await productStore.removeItem(CACHE_META);
    await productStore.removeItem(TIMESTAMP_KEY);
    await productStore.removeItem(IDENTITY_KEY);
  },
};
